import React, { useState, useEffect, useMemo } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Folder as FolderIcon,
  GroupWork as CapacityIcon,
  ConfirmationNumber as TicketsIcon,
  Map as RoadmapsIcon,
  AccountTree as DataMapIcon,
  TrackChanges as ImpactIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import DocHub from './DocHub';
import DataMap from './DataMap';
import Roadmap from './Roadmap';
import CapacityPlanner from './CapacityPlanner';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const SUB_MODULES = [
  {
    id: 'docHub',
    label: 'Doc Hub',
    icon: FolderIcon,
    description: 'Store and organise your PRDs, Roadmaps, Data Maps and other documents.',
  },
  {
    id: 'dataMap',
    label: 'Data Map',
    icon: DataMapIcon,
    description: 'Map your data sources, flows, and dependencies.',
  },
  {
    id: 'capacityTracking',
    label: 'Capacity Planner',
    icon: CapacityIcon,
    description: 'Plan team capacity, sprints, and velocity across your portfolio.',
  },
  {
    id: 'writeTickets',
    label: 'Write My Tickets',
    icon: TicketsIcon,
    description: 'AI-assisted Jira ticket writing from your requirements.',
    disabled: true,
  },
  {
    id: 'roadmaps',
    label: 'Roadmaps',
    icon: RoadmapsIcon,
    description: 'Build and share visual product roadmaps.',
  },
  {
    id: 'impact',
    label: 'Impact View',
    icon: ImpactIcon,
    description: 'Trace how outcomes and applications connect across all PM Mode artifacts.',
  },
];

// ── Traceability / Impact View ────────────────────────────────────────────────

const DOC_STATUS_COLORS = { Draft: '#9e9e9e', 'In Review': '#42a5f5', Approved: '#66bb6a', Archived: '#bdbdbd' };

function ResultCard({ title, color, items, renderItem }) {
  return (
    <Card variant="outlined" sx={{ flex: 1, minWidth: 0 }}>
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: color, flexShrink: 0 }} />
          <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
          <Chip label={items.length} size="small" sx={{ height: 16, fontSize: '0.65rem', ml: 'auto' }} />
        </Box>
        {items.length === 0 ? (
          <Typography variant="caption" color="text.disabled">None linked.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {items.map((item, i) => (
              <React.Fragment key={item.id || i}>
                {i > 0 && <Divider />}
                {renderItem(item)}
              </React.Fragment>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function TraceabilityView({ onOpenMap, onOpenRoadmap }) {
  const [context, setContext] = useState({ outcomes: [], applications: [] });
  const [docs, setDocs] = useState([]);
  const [dmNodes, setDmNodes] = useState([]);
  const [rmNodes, setRmNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState(null); // {type:'outcome'|'app', id, name}
  const [outcomeInput, setOutcomeInput] = useState('');
  const [appInput, setAppInput] = useState('');

  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE}/api/doc-hub/context`),
      fetch(`${API_BASE}/api/doc-hub/documents`),
      fetch(`${API_BASE}/api/data-maps/nodes`),
      fetch(`${API_BASE}/api/roadmaps/nodes`),
    ]).then(async ([ctxRes, docsRes, dmRes, rmRes]) => {
      if (ctxRes.ok) setContext(await ctxRes.json());
      if (docsRes.ok) setDocs(await docsRes.json());
      if (dmRes.ok) setDmNodes(await dmRes.json());
      if (rmRes.ok) setRmNodes(await rmRes.json());
    }).finally(() => setLoading(false));
  }, []);

  const filteredDocs = useMemo(() => {
    if (!selection) return [];
    return docs.filter((d) =>
      selection.type === 'outcome'
        ? (d.links?.outcomes || []).includes(selection.id)
        : (d.links?.applications || []).includes(selection.id)
    );
  }, [docs, selection]);

  const filteredDm = useMemo(() => {
    if (!selection) return [];
    return dmNodes.filter((n) =>
      selection.type === 'outcome'
        ? (n.outcome_links || []).includes(selection.id)
        : (n.app_links || []).includes(selection.id)
    );
  }, [dmNodes, selection]);

  const filteredRm = useMemo(() => {
    if (!selection) return [];
    return rmNodes.filter((n) =>
      selection.type === 'outcome'
        ? (n.outcome_links || []).includes(selection.id)
        : (n.app_links || []).includes(selection.id)
    );
  }, [rmNodes, selection]);

  const totalLinked = filteredDocs.length + filteredDm.length + filteredRm.length;

  return (
    <Box>
      {/* Selector row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Autocomplete
          sx={{ flex: 1, minWidth: 240 }}
          size="small"
          options={context.outcomes || []}
          getOptionLabel={(o) => o.name || ''}
          inputValue={outcomeInput}
          onInputChange={(_, v) => setOutcomeInput(v)}
          onChange={(_, v) => {
            setSelection(v ? { type: 'outcome', id: v.id, name: v.name } : null);
            setAppInput('');
          }}
          renderInput={(params) => <TextField {...params} label="Trace by Outcome" placeholder="Select an outcome…" />}
          renderOption={(props, o) => (
            <li {...props} key={o.id}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2">{o.name}</Typography>
                {(o.status || o.year) && (
                  <Typography variant="caption" color="text.secondary">
                    {[o.status, o.year].filter(Boolean).join(' · ')}
                  </Typography>
                )}
              </Box>
            </li>
          )}
        />
        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', flexShrink: 0 }}>or</Typography>
        <Autocomplete
          sx={{ flex: 1, minWidth: 240 }}
          size="small"
          options={context.applications || []}
          getOptionLabel={(a) => a.name || ''}
          inputValue={appInput}
          onInputChange={(_, v) => setAppInput(v)}
          onChange={(_, v) => {
            setSelection(v ? { type: 'app', id: v.id, name: v.name } : null);
            setOutcomeInput('');
          }}
          renderInput={(params) => <TextField {...params} label="Trace by Application" placeholder="Select an application…" />}
        />
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && !selection && (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <ImpactIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            Select an outcome or application to see all linked documents, data map nodes, and roadmap items.
          </Typography>
        </Box>
      )}

      {!loading && selection && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700}>{selection.name}</Typography>
            <Chip
              label={selection.type === 'outcome' ? 'Outcome' : 'Application'}
              size="small"
              sx={{
                bgcolor: selection.type === 'outcome' ? '#e3f2fd' : '#fff3e0',
                color: selection.type === 'outcome' ? '#0d47a1' : '#bf360c',
                fontWeight: 600,
                fontSize: '0.65rem',
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {totalLinked} linked artifact{totalLinked !== 1 ? 's' : ''}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <ResultCard
              title="Documents"
              color="#4db6ac"
              items={filteredDocs}
              renderItem={(doc) => (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  <Typography variant="caption" fontWeight={600} sx={{ lineHeight: 1.3 }}>{doc.name}</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Chip label={doc.type} size="small" sx={{ height: 14, fontSize: '0.6rem' }} />
                    {doc.status && doc.status !== 'Draft' && (
                      <Chip
                        label={doc.status}
                        size="small"
                        sx={{ height: 14, fontSize: '0.6rem', bgcolor: DOC_STATUS_COLORS[doc.status] + '22', color: DOC_STATUS_COLORS[doc.status] }}
                      />
                    )}
                  </Box>
                </Box>
              )}
            />

            <ResultCard
              title="Data Map Nodes"
              color="#66bb6a"
              items={filteredDm}
              renderItem={(n) => (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                  <Box>
                    <Typography variant="caption" fontWeight={600} sx={{ lineHeight: 1.3 }}>{n.label}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.6rem' }}>{n.map_name}</Typography>
                  </Box>
                  {onOpenMap && (
                    <Tooltip title="Open in Data Map">
                      <Box
                        component="button"
                        onClick={() => onOpenMap(n.map_id)}
                        sx={{ border: 'none', bgcolor: 'transparent', cursor: 'pointer', p: 0, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                      >
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              )}
            />

            <ResultCard
              title="Roadmap Items"
              color="#ffa726"
              items={filteredRm}
              renderItem={(n) => (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                  <Box>
                    <Typography variant="caption" fontWeight={600} sx={{ lineHeight: 1.3 }}>{n.name}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.6rem' }}>
                      {n.roadmap_name}
                      {n.start_date && ` · ${n.start_date}`}
                      {n.end_date && ` → ${n.end_date}`}
                    </Typography>
                  </Box>
                  {onOpenRoadmap && (
                    <Tooltip title="Open in Roadmap">
                      <Box
                        component="button"
                        onClick={() => onOpenRoadmap(n.roadmap_id)}
                        sx={{ border: 'none', bgcolor: 'transparent', cursor: 'pointer', p: 0, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                      >
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              )}
            />
          </Box>
        </>
      )}
    </Box>
  );
}

// ── Coming-soon placeholder ───────────────────────────────────────────────────

function ComingSoonPlaceholder({ module }) {
  const IconComponent = module.icon;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 10,
        gap: 2,
        color: 'text.secondary',
      }}
    >
      <IconComponent sx={{ fontSize: 64, color: '#b2dfdb' }} />
      <Typography variant="h5" fontWeight={600}>{module.label}</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400, textAlign: 'center' }}>
        {module.description}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          mt: 1,
          px: 2,
          py: 0.5,
          borderRadius: 2,
          bgcolor: 'action.hover',
          color: 'text.disabled',
          fontStyle: 'italic',
        }}
      >
        Coming Soon
      </Typography>
    </Box>
  );
}

function PMMode({ onBackToHome, isReadOnly = false, isCompact = false, activeSubModule = 'docHub' }) {
  const initialTab = Math.max(0, SUB_MODULES.findIndex((m) => m.id === activeSubModule));
  const [tab, setTab] = useState(initialTab);
  const [openMapId, setOpenMapId] = useState(null);
  const [openRoadmapId, setOpenRoadmapId] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      const { roadmapId } = e.detail || {};
      if (!roadmapId) return;
      setTab(SUB_MODULES.findIndex((m) => m.id === 'roadmaps'));
      setOpenRoadmapId(roadmapId);
    };
    window.addEventListener('navigate-pmmode-roadmap', handler);
    return () => window.removeEventListener('navigate-pmmode-roadmap', handler);
  }, []);

  const current = SUB_MODULES[tab];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBackToHome}
          variant="text"
          size="small"
        >
          Home
        </Button>
        <Typography variant="h5" fontWeight={600}>PM Mode</Typography>
      </Box>

      {/* Tab bar */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {SUB_MODULES.map((m) => {
            const Icon = m.icon;
            return (
              <Tab
                key={m.id}
                label={m.disabled ? `${m.label} (Coming Soon)` : m.label}
                icon={<Icon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                disabled={!!m.disabled}
                sx={{ textTransform: 'none', minHeight: 48 }}
              />
            );
          })}
        </Tabs>
      </Box>

      {/* Content */}
      {current.id === 'docHub' ? (
        <DocHub
          isReadOnly={isReadOnly}
          isCompact={isCompact}
          onOpenMap={(id) => { setOpenMapId(id); setTab(1); }}
          onOpenRoadmap={(id) => { setOpenRoadmapId(id); setTab(4); }}
        />
      ) : current.id === 'dataMap' ? (
        <DataMap
          isReadOnly={isReadOnly}
          initialMapId={openMapId}
          onMapOpened={() => setOpenMapId(null)}
        />
      ) : current.id === 'roadmaps' ? (
        <Roadmap
          isReadOnly={isReadOnly}
          openRoadmapId={openRoadmapId}
          onRoadmapOpened={() => setOpenRoadmapId(null)}
        />
      ) : current.id === 'capacityTracking' ? (
        <CapacityPlanner isReadOnly={isReadOnly} />
      ) : current.id === 'impact' ? (
        <TraceabilityView
          onOpenMap={(id) => { setOpenMapId(id); setTab(1); }}
          onOpenRoadmap={(id) => { setOpenRoadmapId(id); setTab(4); }}
        />
      ) : (
        <ComingSoonPlaceholder module={current} />
      )}
    </Box>
  );
}

export default PMMode;
